import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FreshnessBadge } from "../components/StatusBadge";
import { CheckCircle2, Clock, Send, RotateCcw, History } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

function NumberField({ label, testid, value, onChange, hint }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
      <input
        data-testid={testid}
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 px-3 py-3 text-lg font-bold outline-none focus:border-teal-600"
      />
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

function UnitReport({ unit, onDone }) {
  const last = unit.last_report || {};
  const [form, setForm] = useState({
    available_beds: last.available_beds ?? 0,
    beds_offline: last.beds_offline ?? 0,
    pending_admissions: last.pending_admissions ?? 0,
    expected_opening_beds: 0,
    expected_opening_date: "",
    temporary_limitations: "",
    transportation_status: last.transportation_status || "",
  });
  const [busy, setBusy] = useState(false);
  const cap = (unit.licensed_beds || 0) + (unit.flex_beds || 0);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const num = (k) => (v) => set(k, v === "" ? "" : Math.max(0, parseInt(v || "0", 10)));

  const submit = async (no_change) => {
    setBusy(true);
    try {
      const payload = no_change
        ? { unit_id: unit.id, no_change: true }
        : {
            unit_id: unit.id,
            available_beds: Number(form.available_beds) || 0,
            beds_offline: Number(form.beds_offline) || 0,
            pending_admissions: Number(form.pending_admissions) || 0,
            expected_opening_beds: Number(form.expected_opening_beds) || 0,
            expected_opening_date: form.expected_opening_date || null,
            temporary_limitations: form.temporary_limitations || null,
            transportation_status: form.transportation_status || null,
          };
      const { data } = await api.post("/reporter/reports", payload);
      if (no_change) toast.success("Confirmed — no change recorded.");
      else toast.success(`Submitted. Public availability: ${data.public_availability} beds. Next update due in ${data.next_deadline_hours}h.`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="font-display text-base font-bold text-slate-900">{unit.name}</div>
          <div className="text-xs text-slate-500">
            Licensed capacity: {unit.licensed_beds}{unit.flex_beds ? ` + ${unit.flex_beds} flex` : ""} · Last update {timeAgo(last.reported_at)}
          </div>
        </div>
      </div>

      <button
        data-testid={`no-change-${unit.id}`}
        onClick={() => submit(true)}
        disabled={busy || !unit.last_report}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-teal-600 bg-teal-50 py-3.5 text-base font-bold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
      >
        <CheckCircle2 size={20} /> No change — confirm current information
      </button>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <NumberField testid={`report-available_beds-${unit.id}`} label="Available staffed beds" value={form.available_beds} onChange={num("available_beds")} hint={`Max operational: ${cap - (Number(form.beds_offline) || 0)}`} />
        <NumberField testid={`report-pending_admissions-${unit.id}`} label="Pending admissions" value={form.pending_admissions} onChange={num("pending_admissions")} />
        <NumberField testid={`report-beds_offline-${unit.id}`} label="Beds offline" value={form.beds_offline} onChange={num("beds_offline")} hint={`Licensed: ${cap}`} />
        <NumberField testid={`report-expected_opening_beds-${unit.id}`} label="Expected openings" value={form.expected_opening_beds} onChange={num("expected_opening_beds")} />
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Opening date</label>
          <input
            data-testid={`report-opening-date-${unit.id}`}
            type="date"
            value={form.expected_opening_date}
            onChange={(e) => set("expected_opening_date", e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-3 text-sm outline-none focus:border-teal-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Transportation</label>
          <select
            data-testid={`report-transport-${unit.id}`}
            value={form.transportation_status}
            onChange={(e) => set("transportation_status", e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-3 text-sm outline-none focus:border-teal-600"
          >
            <option value="">No change</option>
            <option value="available_now">Available now</option>
            <option value="available_with_notice">Available with notice</option>
            <option value="limited">Limited</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </div>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Temporary admission barriers (optional)</label>
        <input
          data-testid={`report-limitations-${unit.id}`}
          value={form.temporary_limitations}
          onChange={(e) => set("temporary_limitations", e.target.value)}
          placeholder="e.g. Awaiting nursing coverage until 3pm"
          className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-600"
        />
      </div>

      <button
        data-testid={`submit-report-${unit.id}`}
        onClick={() => submit(false)}
        disabled={busy}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 py-3.5 text-base font-bold text-white hover:bg-teal-800 disabled:opacity-60"
      >
        <Send size={18} /> Submit report
      </button>
    </div>
  );
}

export default function ReporterDashboard() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/reporter/programs").then((r) => setPrograms(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openHistory = async (pid) => {
    setHistoryFor(pid);
    const { data } = await api.get("/reporter/history", { params: { program_id: pid } });
    setHistory(data);
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Capacity reporting</h1>
      <p className="mt-1 text-sm text-slate-500">Update available staffed beds. Corrections create a new record and never erase prior reports.</p>

      {programs.length === 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          No programs are assigned to your account yet.
        </div>
      )}

      <div className="mt-6 space-y-6">
        {programs.map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{p.facility_name}</div>
                <h2 className="font-display text-xl font-bold text-slate-900">{p.name}</h2>
                <div className="text-xs font-semibold text-teal-700">ASAM {p.asam_level_code}</div>
              </div>
              <div className="flex items-center gap-2">
                <FreshnessBadge status={p.freshness} />
                <button
                  data-testid={`history-${p.id}`}
                  onClick={() => openHistory(p.id)}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <History size={15} /> History
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {(p.units || []).length === 0 && <div className="text-sm text-slate-500">No reporting units configured for this program.</div>}
              {(p.units || []).map((u) => (
                <UnitReport key={u.id} unit={u} onDone={load} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {historyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setHistoryFor(null)}>
          <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">Report history</h3>
              <button onClick={() => setHistoryFor(null)} className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white">Close</button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-bold uppercase tracking-widest text-slate-400">
                  <th className="p-2">When</th><th className="p-2">Available</th><th className="p-2">Pending</th><th className="p-2">Offline</th><th className="p-2">By</th><th className="p-2">Type</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="p-2">{new Date(h.reported_at).toLocaleString()}</td>
                    <td className="p-2 font-bold">{h.available_beds}</td>
                    <td className="p-2">{h.pending_admissions}</td>
                    <td className="p-2">{h.beds_offline}</td>
                    <td className="p-2 text-slate-500">{h.reported_by_email}</td>
                    <td className="p-2">{h.no_change ? <span className="inline-flex items-center gap-1 text-teal-700"><RotateCcw size={12} />no-change</span> : "update"}</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td className="p-4 text-slate-500" colSpan={6}>No reports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
